import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils.ts";
import { Button } from "@/components/ui/button.tsx";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore.tsx";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

interface Subject {
  id: string;
  name: string;
  icon: string;
}

interface SubjectSelectProps {
  subjects: Subject[];
  onSelect: (selectedSubjectIds: string[]) => void;
  defaultSubjectIds: string[];
}

export function SubjectSelect({ subjects, onSelect, defaultSubjectIds }: SubjectSelectProps) {
  const [open, setOpen] = React.useState(false);
  const { profileData } = useProfileDataStore();
  const { t } = useTranslation(["profile", "common"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const [selectedSubjectIds, setSelectedSubjectIds] = React.useState<string[]>(defaultSubjectIds);

  const handleSelect = (subject: Subject) => {
    const newSelectedSubjectIds = selectedSubjectIds.includes(subject.id)
      ? selectedSubjectIds.filter((id) => id !== subject.id)
      : [...selectedSubjectIds, subject.id];

    setSelectedSubjectIds(newSelectedSubjectIds);
    onSelect(newSelectedSubjectIds);
  };
  const isMobile = useIsMobile();
  const shownItems = isMobile ? 1 : 3;

  const selectedSubjects = subjects.filter((subject) => selectedSubjectIds.includes(subject.id));

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className="h-fit w-full justify-between border-none bg-transparent"
          role="combobox"
          variant="outline"
        >
          <div className="flex flex-wrap gap-1">
            {selectedSubjects.length > 0 ? (
              selectedSubjects.slice(0, shownItems).map((subject) => (
                <Badge
                  key={subject.id}
                  className="flex items-center gap-1 truncate bg-blue-500/5 px-3 py-2 text-blue-300 backdrop-blur-sm hover:bg-blue-500/10 max-sm:max-w-32"
                  variant="default"
                >
                  <Icon height={"1rem"} icon={subject.icon} width={"1rem"} />
                  {subject.name}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">
                {t("profile:selectSubjects.selectSubjects")}
              </span>
            )}

            {selectedSubjects.length > shownItems && (
              <Badge
                className="flex items-center gap-1 bg-blue-500/5 px-3 py-2 text-blue-300 backdrop-blur-sm hover:bg-blue-500/10"
                variant="default"
              >
                +{selectedSubjects.length - shownItems}
              </Badge>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[1505] w-[var(--radix-popover-trigger-width)] min-w-[100%] p-0">
        <Command>
          <CommandInput placeholder={t("profile:selectSubjects.searchSubjects")} />
          <CommandList>
            <CommandEmpty>{t("profile:selectSubjects.noSubjectsAvailable")}</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="text-muted-foreground h-60 pb-2">
                {subjects.map((subject) => (
                  <CommandItem
                    key={subject.id}
                    className={
                      "hover:bg-muted/50 aria-[selected=true]:bg-side-accent! rounded-none! bg-transparent!"
                    }
                    onSelect={() => handleSelect(subject)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon height={"1rem"} icon={subject.icon} width={"1rem"} />
                      {subject.name}
                    </div>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        selectedSubjectIds.includes(subject.id) ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
